import type { ComparisonData } from "../types";

export const heygenComparison: ComparisonData = {
  slug: "heygen-vs-todo-ai-talking-avatar",
  competitorName: "HeyGen",
  competitorCategory: "AI Talking-Avatar Platform",
  todoAiFlowPath: "/talking-avatar",
  title: "HeyGen vs TodoAI Talking Avatar — Side-by-Side Comparison",
  metaDescription:
    "Compare HeyGen and TodoAI's Talking Avatar in 2026: pricing, Hebrew lip-sync, ease of use, credits model, and which is right for solo creators and small businesses. Updated May 2026.",

  introTodoAi:
    "TodoAI's Talking Avatar flow turns a single photo into a talking video with automatic lip-sync. The owner uploads a face, writes (or records) the script, and gets a finished video in minutes — no avatar library to navigate, no per-minute video tier to monitor. Part of a 9-flow studio that also covers images, video generation, and product shots.",

  introCompetitor:
    "HeyGen is a US-based AI video platform built around customizable avatars, voice cloning, and translation. It offers a large avatar library, brand-kit features, and team workspaces. Pricing scales by minutes of finished video per month, and its core market is corporate L&D, marketing teams, and YouTubers — not Hebrew-first solo creators.",

  rows: [
    {
      label: "Free tier",
      todoAi: "Yes — 100 credits/month, enough for several short avatars",
      competitor: "Limited — 3 min/month at 720p, watermarked",
      highlight: "todoAi",
    },
    {
      label: "Starting paid price",
      todoAi: "₪35 / ~$10 per month (Starter)",
      competitor: "$29/month (Creator, billed annually)",
      highlight: "todoAi",
    },
    {
      label: "Hebrew lip-sync",
      todoAi: "Yes — Hebrew script supported with proper RTL lip movement",
      competitor: "Limited — Hebrew is supported but not a focus",
      highlight: "todoAi",
    },
    {
      label: "Interface language",
      todoAi: "Bilingual Hebrew + English",
      competitor: "English only",
      highlight: "todoAi",
    },
    {
      label: "Source of the avatar",
      todoAi: "Any photo you upload — instant",
      competitor: "Pick from library or record a custom avatar (~paid plans)",
      highlight: "tie",
    },
    {
      label: "Pricing model",
      todoAi: "Credit-based — pay per generation, no time cap",
      competitor: "Minute-based — capped video minutes per month",
      highlight: "todoAi",
    },
    {
      label: "Additional AI flows in the same account",
      todoAi: "Yes — 9 flows: image, video, product, profile photo, embeddable chatbot, etc.",
      competitor: "Talking avatars only (some translation features)",
      highlight: "todoAi",
    },
    {
      label: "Embeddable chatbot for your site",
      todoAi: "Yes — Bot Builder included on Pro/Creator",
      competitor: "No",
      highlight: "todoAi",
    },
    {
      label: "Best for",
      todoAi: "Hebrew-speaking creators, course teachers, and small businesses making short videos from photos",
      competitor: "English-speaking corporate teams and YouTubers producing long-form video content",
      highlight: "tie",
    },
  ],

  stats: [
    {
      value: "Yes",
      description: "Hebrew RTL lip-sync support in TodoAI's Talking Avatar flow",
    },
    {
      value: "1 photo",
      description: "What you need to create a talking avatar on TodoAI (vs. picking from a HeyGen avatar library)",
    },
    {
      value: "₪35/mo",
      description: "TodoAI Starter price — the cheapest paid talking-avatar plan with Hebrew support",
    },
    {
      value: "$29/mo",
      description: "HeyGen Creator price, billed annually, in USD",
      source: "https://www.heygen.com/pricing",
      sourceLabel: "HeyGen pricing page",
    },
  ],

  faqs: [
    {
      question: "Does TodoAI support Hebrew talking avatars?",
      answer:
        "Yes — TodoAI is Hebrew-first, and the Talking Avatar flow supports Hebrew scripts end-to-end (text input in Hebrew, RTL UI, Hebrew-aware lip movement). HeyGen technically supports Hebrew, but the product is built for English-speaking teams.",
    },
    {
      question: "Can I use my own photo as the avatar on both?",
      answer:
        "Yes on TodoAI — upload any photo and the flow turns it into a talking video instantly. On HeyGen, custom avatars (your own face) typically require recording a video sample and waiting for processing; the free tier uses library avatars only.",
    },
    {
      question: "Which is cheaper for a small business?",
      answer:
        "TodoAI's Starter plan is ₪35 (~$10)/month with 500 credits — enough for many short avatars and image generations. HeyGen's lowest plan with custom avatars is $29/month billed annually ($348/year). For Hebrew-speaking small businesses, TodoAI is materially cheaper.",
    },
    {
      question: "Does TodoAI have an avatar library like HeyGen?",
      answer:
        "No — TodoAI works directly from a photo you upload. This is by design: it's faster and avoids the licensing/personalization friction of a fixed library. If you need a polished, pre-made library of speaking avatars, HeyGen is the better fit.",
    },
    {
      question: "Can I generate other content types on the same account?",
      answer:
        "On TodoAI, yes — the same account includes image generation, product shots, profile photos, text-to-video, and an embeddable AI chatbot. HeyGen is focused specifically on video and talking avatars.",
    },
  ],

  lastUpdated: "2026-05-26",
};
